import React from "react";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";
import { styled } from "@mui/material/styles";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { getAddressWithoutCountry } from "utils/addressUtils";
import Guard from "components/Guard/Guard";
import { IParticipantEntity, isParticipantModifiedByOI } from "models/Participant.model";
import { formatDateStr } from "utils/formatDateStr";
import { DATE_FORMATS } from "constants/configs";
import IntelligenceTag from "components/IntelligenceTag/IntelligenceTag";
import ActionItemTooltip from "components/ActionSection/ActionItemTooltip";
import { ClickAwayListener } from "@mui/material";
import IntelligenceTooltipContent from "components/IntelligenceTag/IntelligenceTooltipContent";

type Props = {
  participant: IParticipantEntity;
};

const ParticipantCardContact: React.FC<Props> = ({ participant }) => {
  const { t } = useTranslation();

  const isModifiedByOI = isParticipantModifiedByOI(participant.modifiedBy || "");
  const modifiedDate = formatDateStr(participant.modifiedDate || "", DATE_FORMATS.FULL_DATE);
  const participantAddress = React.useMemo(() => getAddressWithoutCountry(participant.address.fullAddress), [
    participant,
  ]);

  const [isIntelligenceTooltipVisible, setIsIntelligenceTooltipVisible] = React.useState(false);

  const handleCloseIntelligenceTooltip = React.useCallback(() => {
    setIsIntelligenceTooltipVisible(false);
  }, []);

  const handleOpenIntelligenceTooltip = React.useCallback(() => {
    setIsIntelligenceTooltipVisible(true);
  }, []);

  if (!participant) {
    return null;
  }

  return (
    <StyledWrapper data-testid={`ParticipantContact-${participant.id}`}>
      <Guard condition={participant.phone}>
        <StyledParticipantInfo $disabled={participant.isDeleted}>
          <FontIcon name={"i-toggle-phone"} />
          {participant.phone}
        </StyledParticipantInfo>
      </Guard>

      <Guard condition={participant.email}>
        <StyledParticipantInfo $disabled={participant.isDeleted}>
          <FontIcon name={"i-tech-email"} />
          {participant.email}
        </StyledParticipantInfo>
      </Guard>

      <Guard condition={participantAddress}>
        <StyledParticipantInfo $disabled={participant.isDeleted}>
          <FontIcon name={"i-travel-location"} />
          {participantAddress}
        </StyledParticipantInfo>
      </Guard>

      <StyledParticipantInfo $disabled={participant.isDeleted}>
        <strong>{t("Level of access: ")}</strong>
        {participant.accessLevel}
      </StyledParticipantInfo>

      <Guard condition={participant.modifiedBy && participant.modifiedDate}>
        <StyledParticipantInfo $disabled={participant.isDeleted}>
          <strong>{t("Last modified: ")}</strong>
          <Guard
            condition={isModifiedByOI}
            fallback={t(`{{modifiedDate}}, by {{modifiedBy}}`, {
              modifiedDate,
              modifiedBy: participant.modifiedBy,
            })}
          >
            {t("{{modifiedDate}}, by ", { modifiedDate })}

            <ClickAwayListener onClickAway={handleCloseIntelligenceTooltip}>
              <div>
                <ActionItemTooltip
                  title={<IntelligenceTooltipContent onClose={handleCloseIntelligenceTooltip} />}
                  onClose={handleCloseIntelligenceTooltip}
                  open={isIntelligenceTooltipVisible}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                >
                  <IntelligenceTag onClick={handleOpenIntelligenceTooltip} />
                </ActionItemTooltip>
              </div>
            </ClickAwayListener>
          </Guard>
        </StyledParticipantInfo>
      </Guard>
    </StyledWrapper>
  );
};

const StyledParticipantInfo = styled("div", { shouldForwardProp: (prop) => prop !== "$disabled" })<{
  $disabled?: boolean;
}>(
  ({ theme, $disabled }) => css`
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 130%;
    color: ${theme.palette.text.secondary};
    ${theme.mixins.flexGap("12px")}

    strong {
      color: ${theme.palette.text.primary};
      font-weight: 500;
    }

    ${$disabled &&
    `
      &, strong {
        color: ${theme.palette.grey[700]};
      }
    `}
  `
);

const StyledWrapper = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: 16px 32px;
    ${theme.mixins.flexGap("12px")}
  `
);

export default ParticipantCardContact;
